import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import type { AdminGuideStatusGroup } from '@/features/admin/api/admin.api';

type Tone = 'blue' | 'green' | 'amber';

const TONES: Record<Tone, { fg: string; bg: string; ring: string }> = {
  blue: { fg: '#2563EB', bg: '#EFF6FF', ring: '#BFDBFE' },
  green: { fg: 'var(--success)', bg: 'var(--success-light)', ring: '#BBF7D0' },
  amber: { fg: 'var(--gold-dark)', bg: 'var(--warning-light)', ring: '#FDE68A' },
};

interface StatProps {
  label: string;
  value?: number;
  hint: string;
  icon: ReactNode;
  tone: Tone;
  active: boolean;
  onClick: () => void;
}

function Stat({ label, value, hint, icon, tone, active, onClick }: StatProps) {
  const colors = TONES[tone];
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={active}
      sx={{
        width: '100%',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: active ? colors.ring : 'divider',
        borderRadius: 'var(--radius-2xl)',
        boxShadow: active ? `0 0 0 3px ${colors.bg}, var(--shadow-sm)` : 'var(--shadow-xs)',
        transition: 'border-color .2s ease, box-shadow .2s ease',
        '&:hover': { borderColor: colors.ring, boxShadow: 'var(--shadow-sm)' },
        '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${colors.ring}` },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {label}
        </Typography>
        {value == null ? (
          <Skeleton width={56} height={36} />
        ) : (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: '1.75rem',
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value.toLocaleString('en-IN')}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'textColors.muted' }}>
          {hint}
        </Typography>
      </Box>
      <Box
        aria-hidden
        sx={{
          width: 40,
          height: 40,
          borderRadius: 'var(--radius-xl)',
          bgcolor: colors.bg,
          color: colors.fg,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          '& .MuiSvgIcon-root': { fontSize: 22 },
        }}
      >
        {icon}
      </Box>
    </ButtonBase>
  );
}

interface GuideDirectoryStatsProps {
  total?: number;
  approved?: number;
  pending?: number;
  statusGroup: AdminGuideStatusGroup | '';
  onSelect: (group: AdminGuideStatusGroup | '') => void;
}

export function GuideDirectoryStats({ total, approved, pending, statusGroup, onSelect }: GuideDirectoryStatsProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
        mb: 3,
      }}
    >
      <Stat
        label="Total Guides"
        value={total}
        hint="All registered profiles"
        icon={<GroupsOutlinedIcon />}
        tone="blue"
        active={statusGroup === ''}
        onClick={() => onSelect('')}
      />
      <Stat
        label="Approved Guides"
        value={approved}
        hint="Verified and approved"
        icon={<VerifiedOutlinedIcon />}
        tone="green"
        active={statusGroup === 'approved'}
        onClick={() => onSelect(statusGroup === 'approved' ? '' : 'approved')}
      />
      <Stat
        label="Pending Review"
        value={pending}
        hint="Submitted, awaiting a decision"
        icon={<HourglassEmptyRoundedIcon />}
        tone="amber"
        active={statusGroup === 'pending_review'}
        onClick={() => onSelect(statusGroup === 'pending_review' ? '' : 'pending_review')}
      />
    </Box>
  );
}
